// Print All — register selection dialog (Hardware / Complaint / AMC /
// Connectivity / Service / ALL) with Preview, Print and PDF Export.
import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Printer, FileDown, Eye, Loader2, Stamp, Trash2 } from "lucide-react";
import { PRINT_OPTIONS, sectionsForOption, registerBlobUrl, downloadRegisterPDF } from "@/lib/registerPdf";
import { fetchBundle, PdfPreviewDialog } from "./shared";

const FILE_NAMES = {
  hardware: "Hardware_Register.pdf",
  complaints: "Complaint_Call_Logging_Register.pdf",
  amc: "AMC_Register.pdf",
  connectivity: "Connectivity_Register.pdf",
  service: "Service_Register.pdf",
  all: "All_Registers.pdf",
};

export default function PrintAllDialog({ open, onClose }) {
  const [choice, setChoice] = useState("all");
  const [covers, setCovers] = useState(true);
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [sealBusy, setSealBusy] = useState(false);
  const sealInputRef = useRef(null);

  const uploadSeal = async (file) => {
    if (!file) return;
    if (file.size > 1_200_000) { toast.error("Seal image must be under 1.2 MB"); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      setSealBusy(true);
      try {
        await api.post("/registers/seal", { image: reader.result });
        setBundle(b => (b ? { ...b, seal_image: reader.result } : b));
        toast.success("Seal saved — it will print inside the cover-page seal box");
      } catch {
        toast.error("Seal upload failed");
      } finally {
        setSealBusy(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const removeSeal = async () => {
    setSealBusy(true);
    try {
      await api.delete("/registers/seal");
      setBundle(b => (b ? { ...b, seal_image: "" } : b));
      toast.success("Seal removed");
    } catch {
      toast.error("Remove failed");
    } finally {
      setSealBusy(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    fetchBundle()
      .then(setBundle)
      .catch(() => toast.error("Could not load register data"))
      .finally(() => setLoading(false));
  }, [open]);

  const sections = sectionsForOption(choice);
  const fname = FILE_NAMES[choice] || "Registers.pdf";

  return (
    <>
      <Dialog open={open} onOpenChange={v => !v && onClose()}>
        <DialogContent className="max-w-lg" data-testid="print-all-dialog">
          <DialogHeader>
            <DialogTitle>Print All — Register Selection</DialogTitle>
            <DialogDescription>Which Register do you want to print?</DialogDescription>
          </DialogHeader>
          {loading ? (
            <div className="py-8 flex items-center justify-center text-muted-foreground gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading register data…
            </div>
          ) : (
            <div className="space-y-2" data-testid="print-all-options">
              {PRINT_OPTIONS.map(o => (
                <label key={o.id}
                  className={`flex items-start gap-3 border rounded-lg p-3 cursor-pointer transition-colors ${choice === o.id ? "border-brand-blue bg-brand-blue/5" : "border-border hover:bg-muted/40"}`}
                  data-testid={`print-option-${o.id}`}>
                  <input type="radio" name="print-all" className="mt-1" checked={choice === o.id} onChange={() => setChoice(o.id)} />
                  <span>
                    <span className="block text-sm font-semibold">{o.label}</span>
                    <span className="block text-xs text-muted-foreground">{o.hint}</span>
                  </span>
                </label>
              ))}
            </div>
          )}
          <label className="flex items-center gap-2 mt-1 text-sm cursor-pointer" data-testid="print-all-covers">
            <input type="checkbox" checked={covers} onChange={e => setCovers(e.target.checked)} />
            <span>Include cover pages with seal &amp; opening/closing certificates</span>
          </label>
          {/* Local Body seal — prints inside the cover-page seal box */}
          <div className="flex items-center gap-3 border border-border rounded-lg p-2.5" data-testid="seal-manager">
            <input ref={sealInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
              data-testid="seal-file-input"
              onChange={e => { uploadSeal(e.target.files?.[0]); e.target.value = ""; }} />
            {bundle?.seal_image ? (
              <img src={bundle.seal_image} alt="Local Body seal" className="h-12 w-12 object-contain border border-border rounded" data-testid="seal-preview" />
            ) : (
              <div className="h-12 w-12 border border-dashed border-border rounded flex items-center justify-center text-muted-foreground">
                <Stamp className="w-5 h-5" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold">Local Body Seal</p>
              <p className="text-[11px] text-muted-foreground">{bundle?.seal_image ? "Uploaded — prints on cover pages" : "Upload your official seal (PNG/JPG, < 1.2 MB)"}</p>
            </div>
            <Button variant="outline" size="sm" disabled={sealBusy || loading} data-testid="seal-upload-btn"
              onClick={() => sealInputRef.current?.click()}>
              {bundle?.seal_image ? "Replace" : "Upload"}
            </Button>
            {bundle?.seal_image && (
              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600" disabled={sealBusy} onClick={removeSeal} data-testid="seal-remove-btn">
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
          <DialogFooter className="gap-2 flex-wrap">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button variant="outline" disabled={!bundle || loading} data-testid="print-all-preview"
              onClick={() => setPreviewUrl(registerBlobUrl(sections, bundle, { covers }))}>
              <Eye className="w-4 h-4 mr-1.5" /> Preview
            </Button>
            <Button variant="outline" disabled={!bundle || loading} data-testid="print-all-pdf"
              onClick={() => downloadRegisterPDF(sections, bundle, fname, { covers })}>
              <FileDown className="w-4 h-4 mr-1.5" /> PDF
            </Button>
            <Button className="bg-brand-blue hover:bg-brand-blue/90 text-white" disabled={!bundle || loading} data-testid="print-all-print"
              onClick={() => setPreviewUrl(registerBlobUrl(sections, bundle, { covers }))}>
              <Printer className="w-4 h-4 mr-1.5" /> Print
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <PdfPreviewDialog url={previewUrl} onClose={() => setPreviewUrl("")} title={fname.replace(/\.pdf$/i, "").replace(/_/g, " ")} />
    </>
  );
}
